import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import DOMPurify from 'dompurify';
import { getActiveStep, selectActiveLayer, setActiveLayer } from '../../../../../../../redux/toolsSlice';
import { removeLayerConfig, setLayerConfig, updateLayer } from '../../../../../../../redux/stepsSlice';
import RichTextOption from '../../../../../../organisms/editor-design/options/RichTextOption';

// Use inline-style attributors so font/size/alignment are emitted as portable
// inline CSS (renders in the learner view without Quill's stylesheet). Guarded —
// harmless where window.Quill isn't present (e.g. server render / learner bundle).
if (typeof window !== 'undefined' && window.Quill) {
  try {
    window.Quill.register(window.Quill.import('attributors/style/align'), true);
    window.Quill.register(window.Quill.import('attributors/style/font'), true);
    window.Quill.register(window.Quill.import('attributors/style/size'), true);
  } catch (_e) { /* no-op */ }
}

const BODY_TOOLBAR = [
  [{ font: [] }],
  [{ size: ['small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ align: [] }],
  ['link'],
  ['clean'],
];

export const BodyTextLayerContentDefaults = (id) => ({
  id,
  type: 'body',
  fontSize: 14,
  color: '#000000',
  fontFamily: 'Open Sans',
  content: '<p>Write your paragraph here…</p>',
});

// Captures the starting HTML ONCE (via useState initializer) so the editor is
// uncontrolled after mount. Feeding a value that changes on every keystroke makes
// Quill re-paste and lose the caret / swallow Enter — this avoids that entirely.
const BodyRichEditor = ({ initialHtml, onChange }) => {
  const [initial] = useState(initialHtml);
  return (
    <RichTextOption
      optionId="body"
      defaultValue={initial}
      onChange={onChange}
      toolbarOptions={BODY_TOOLBAR}
    />
  );
};

function BodyTextContent({ layer }) {
  const dispatch = useDispatch();
  const activeStep = useSelector((state) => getActiveStep(state));
  const stepId = activeStep.id;
  const activeLayer = useSelector((state) => selectActiveLayer(state));
  const isEditing = activeLayer && activeLayer.id == layer.id;

  const editElement = () => {
    dispatch(setActiveLayer(layer));
    dispatch(setLayerConfig([{ layerId: layer.id, sectionId: 'body', sectionTitle: 'Text', values: [] }]));
  };

  const onChange = (html) => {
    dispatch(updateLayer({ stepId, layer: { ...layer, content: html } }));
  };

  if (isEditing) {
    return (
      <div className="container-large-text un-body-rte">
        <BodyRichEditor key={layer.id} initialHtml={layer.content || ''} onChange={onChange} />
      </div>
    );
  }

  return (
    <div className="container-large-text">
      <BodyTextContentPreview layer={layer} EditElement={editElement} />
    </div>
  );
}

export const BodyTextContentPreview = ({ layer, EditElement }) => {
  const wrapStyle = {
    fontSize: layer.fontSize,
    color: layer.fill,
    fontFamily: layer.font,
    textAlign: layer.textAlign || 'left',
  };
  const raw = layer.content || '';
  const isHtml = /<[a-z][\s\S]*>/i.test(raw);
  const html = DOMPurify.sanitize(isHtml ? raw : raw.replace(/\n/g, '<br>'));
  return (
    <div
      className="un-body-html"
      tabIndex={0}
      onClick={EditElement}
      style={wrapStyle}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default BodyTextContent;
